import { FC, useEffect, useRef, useState } from 'react';

import useRentAvailabilityMutation from 'api/mutations/useRentAvailabilityMutation';
import HalfDayCalendar, {
  HalfDaySelection,
} from 'components/form/HalfDayCalendar';
import DateIcon from 'components/icons/DateIcon';
import CommonModal, { ModalRef } from 'components/modals/CommonModal';
import Button from 'components/primitives/Button';
import { ErrorMessage, Form, Formik } from 'formik';
import useAuthModal from 'hooks/useAuthModal';
import { useNavigate } from 'react-router-dom';
import { $session } from 'state/stores';
import { $itemRentDetails } from 'state/stores/rent-details';
import DateTimeZ from 'utils/dateTime';
import { validationSchema } from '../ProductToRent/utils';
import { RentFormProps } from './RentForm.props';

// TODO: Display validation messages when user selects dates that have been marked as unavailable by the owner
// TODO: Find a better way to display date and timeblock selection
const RentFormView: FC<RentFormProps> = ({
  handleRentClick,
  isRentNowDisabled = false,
  submitButtonTitle = 'Rent Now',
}) => {
  const [dateRange, setDateRange] = useState<(Date | null)[]>([]);
  const [pickupTimeBlock, setPickupTimeBlock] = useState<'am' | 'pm' | null>(
    null,
  );
  const [dropoffTimeBlock, setDropoffTimeBlock] = useState<'am' | 'pm' | null>(
    null,
  );

  const rentAvailability = $itemRentDetails.selectors.useRentAvailability();
  const product = $itemRentDetails.selectors.useProduct();
  const itemId = product?._id;
  const initialValues = {
    dateRange: dateRange, // Set initial value as the date range
    pickupTimeBlock: pickupTimeBlock,
    dropoffTimeBlock: dropoffTimeBlock,
  };
  const navigate = useNavigate();
  const { openAuthModal } = useAuthModal();

  const { mutateAsync: fetchRentAvailability, isPending } =
    useRentAvailabilityMutation();

  const isActive = $session.selectors.useIsAuth();

  // Prefetch availability for next 6 months on mount to power the calendar
  useEffect(() => {
    // Only prefetch availability for authenticated users to avoid triggering auth flows during browsing
    if (!itemId || !isActive) return;
    const currentDate = new DateTimeZ().defaultPayloadStringFormat();
    const endDate = new DateTimeZ().addMonths(6).defaultPayloadStringFormat();
    fetchRentAvailability({ id: itemId, startDate: currentDate, endDate }).then(
      (data: any) => {
        $itemRentDetails.actions.setRentAvailability(data);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, isActive]);

  const disabledDates =
    rentAvailability?.map((e) => {
      const eDate = new Date(e.date);
      return (
        eDate.getFullYear() + '-' + eDate.getMonth() + '-' + eDate.getDate()
      );
    }) ?? [];

  /**
   * Checks if a given date is disabled.
   *
   * @param {Date} date - The date to check.
   * @return {boolean} Returns true if the date is disabled, false otherwise.
   */
  const disableDates = (date: Date) => {
    return disabledDates.includes(
      date.getFullYear() + '-' + date.getMonth() + '-' + date.getDate(),
    );
  };

  /**
   * Checks if the item is available for rent within the specified date range.
   *
   * @return {boolean} True if the item is available, false otherwise.
   */
  const isItemAvailable = (newDates?: string[]) => {
    let startDate = new Date(dateRange![0]!); // Start from the startDate
    let endDate = new Date(dateRange![1]!); // Start from the startDate
    let disabledDatesMap = new Set();
    let dates = newDates ?? disabledDates;
    for (let i = 0; i < dates.length; i++) disabledDatesMap.add(dates[i]);
    if (disabledDatesMap.size === 0) return true;
    while (startDate <= endDate) {
      const startDateString =
        startDate.getFullYear() +
        '-' +
        startDate.getMonth() +
        '-' +
        startDate.getDate();
      if (disabledDatesMap.has(startDateString)) {
        return false;
      }
      startDate.setDate(startDate.getDate() + 1);
    }
    return true;
  };

  // Build availability map for HalfDayCalendar (AM/PM) from product weekly schedule and disabledDates
  const availabilityMap = (() => {
    const map: Record<string, { am: boolean; pm: boolean }> = {};
    const today = new Date();
    const end = new Date(today);
    end.setMonth(end.getMonth() + 6);
    const weekly = product?.weeklySchedule;

    const disabledSet = new Set(disabledDates);

    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    const ymd = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    for (let d = new Date(today); d <= end; d.setDate(d.getDate() + 1)) {
      const key = ymd(d);
      if (
        disabledSet.has(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`)
      ) {
        map[key] = { am: false, pm: false };
        continue;
      }
      if (!weekly) {
        map[key] = { am: true, pm: true };
        continue;
      }
      const day = d.getDay();
      const daySchedule = weekly[day as keyof typeof weekly];
      map[key] = {
        am: Boolean(daySchedule?.am),
        pm: Boolean(daySchedule?.pm),
      };
    }
    return map;
  })();

  // Modal ref for calendar popup
  const modalRef = useRef<ModalRef>(null);

  /**
   * Validates if the selected pickup and dropoff times are available based on the product's weekly schedule
   */
  const validateTimeAvailability = (
    startDate: Date,
    endDate: Date,
    pickupTime: 'am' | 'pm' | null,
    dropoffTime: 'am' | 'pm' | null,
  ) => {
    if (!product?.weeklySchedule || !pickupTime || !dropoffTime) {
      return {
        isValid: false,
        error: 'Please select pickup and dropoff times',
      };
    }

    const weeklySchedule = product.weeklySchedule;

    // Check pickup time availability
    const pickupDay = startDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const pickupDaySchedule =
      weeklySchedule[pickupDay as keyof typeof weeklySchedule];

    if (
      !pickupDaySchedule ||
      !pickupDaySchedule[pickupTime as keyof typeof pickupDaySchedule]
    ) {
      return {
        isValid: false,
        error: `Pickup time (${pickupTime.toUpperCase()}) is not available on ${getDayName(pickupDay)}`,
      };
    }

    // Check dropoff time availability
    const dropoffDay = endDate.getDay();
    const dropoffDaySchedule =
      weeklySchedule[dropoffDay as keyof typeof weeklySchedule];

    if (
      !dropoffDaySchedule ||
      !dropoffDaySchedule[dropoffTime as keyof typeof dropoffDaySchedule]
    ) {
      return {
        isValid: false,
        error: `Dropoff time (${dropoffTime.toUpperCase()}) is not available on ${getDayName(dropoffDay)}`,
      };
    }

    return { isValid: true, error: null };
  };

  /**
   * Helper function to get day name from day number
   */
  const getDayName = (dayNumber: number): string => {
    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    return days[dayNumber];
  };

  /**
   * Gets available time blocks for a given date
   */
  const getAvailableTimeBlocks = (date: Date): { am: boolean; pm: boolean } => {
    if (!product?.weeklySchedule) return { am: false, pm: false };

    const day = date.getDay();
    const daySchedule =
      product.weeklySchedule[day as keyof typeof product.weeklySchedule];

    return {
      am: daySchedule?.am || false,
      pm: daySchedule?.pm || false,
    };
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (data, { setErrors }) => {
        const startDate = new DateTimeZ(data.dateRange[0]?.toDateString());
        const endDate = new DateTimeZ(data.dateRange[1]?.toDateString());

        // Validate time availability first
        const timeValidation = validateTimeAvailability(
          data.dateRange[0]!,
          data.dateRange[1]!,
          data.pickupTimeBlock,
          data.dropoffTimeBlock,
        );

        if (!timeValidation.isValid) {
          setErrors({
            pickupTimeBlock: timeValidation.error || 'Invalid pickup time',
            dropoffTimeBlock: timeValidation.error || 'Invalid dropoff time',
          });
          return;
        }

        // if (startDate.formatDate() === endDate.formatDate()) {
        //   setErrors({
        //     dateRange:
        //       'Please adjust the dates to meet the 2-day minimum rental period',
        //   });
        //   return;
        // }

        const currentDate = new DateTimeZ().defaultPayloadStringFormat();
        fetchRentAvailability({
          id: itemId!,
          startDate: currentDate,
          endDate: endDate.addMonths(6).defaultPayloadStringFormat(),
        }).then((data: any) => {
          $itemRentDetails.actions.setRentAvailability(data);
          const dates = data.map((e: any) => {
            const eDate = new Date(e.date);
            return (
              eDate.getFullYear() +
              '-' +
              eDate.getMonth() +
              '-' +
              eDate.getDate()
            );
          });
          const isAvailable = isItemAvailable(dates);

          if (!isAvailable) {
            setErrors({
              dateRange: 'Item is not available for the selected dates',
            });
            return;
          }
          handleRentClick &&
            handleRentClick({
              ...dateRange,
              pickupTimeBlock,
              dropoffTimeBlock,
            });
        });
      }}>
      {({ errors, setFieldValue }) => {
        return (
          <div className="w-full">
            <Form className="flex lg:flex-row flex-col gap-4">
              <div className="w-full lg:w-[60%]">
                <button
                  type="button"
                  onClick={() => modalRef.current?.onOpen?.()}
                  className="btn p-0 w-full">
                  <div className="flex gap-4 h-full w-full">
                    <div className="bg-secondary-900 h-full w-12 rounded-l-md flex justify-center items-center">
                      <DateIcon />
                    </div>
                    <div className="flex flex-col justify-center items-start text-left font-light text-gray-600 mr-4 py-3">
                      <div className="text-xs text-gray-500">Rental window</div>
                      <div className="text-sm text-gray-800">
                        {(() => {
                          if (!dateRange?.[0] || !dateRange?.[1])
                            return 'Select dates and time blocks';
                          const fmt = (d: Date) =>
                            d.toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            });
                          const pickup = pickupTimeBlock
                            ? pickupTimeBlock.toUpperCase()
                            : '—';
                          const dropoff = dropoffTimeBlock
                            ? dropoffTimeBlock.toUpperCase()
                            : '—';
                          return `${fmt(dateRange[0])} (${pickup}) → ${fmt(dateRange[1])} (${dropoff})`;
                        })()}
                      </div>
                    </div>
                  </div>
                </button>
                <CommonModal ref={modalRef} className="max-w-6xl">
                  <HalfDayCalendar
                    availability={availabilityMap}
                    initialMonth={new Date()}
                    minDate={new Date()}
                    onContinue={() => {
                      modalRef.current?.onClose?.();
                    }}
                    onChange={(blocks: HalfDaySelection) => {
                      const keys = Object.keys(blocks)
                        .filter((k) => blocks[k].am || blocks[k].pm)
                        .sort();
                      if (keys.length === 0) {
                        setDateRange([] as unknown as Date[]);
                        setPickupTimeBlock(null);
                        setDropoffTimeBlock(null);
                        setFieldValue('dateRange', []);
                        setFieldValue('pickupTimeBlock', null);
                        setFieldValue('dropoffTimeBlock', null);
                        return;
                      }
                      const start = new Date(keys[0]);
                      const end = new Date(keys[keys.length - 1]);
                      setDateRange([start, end]);
                      setFieldValue('dateRange', [start, end]);

                      const startHalves = blocks[keys[0]];
                      const endHalves = blocks[keys[keys.length - 1]];
                      const pickup = startHalves.am
                        ? 'am'
                        : startHalves.pm
                          ? 'pm'
                          : null;
                      const dropoff = endHalves.pm
                        ? 'pm'
                        : endHalves.am
                          ? 'am'
                          : null;
                      setPickupTimeBlock(pickup as 'am' | 'pm' | null);
                      setDropoffTimeBlock(dropoff as 'am' | 'pm' | null);
                      setFieldValue('pickupTimeBlock', pickup);
                      setFieldValue('dropoffTimeBlock', dropoff);
                    }}
                  />
                </CommonModal>
              </div>
              <div className="flex items-center">
                <div className="">
                  <Button
                    isLoading={isPending}
                    isDisabled={isRentNowDisabled}
                    variant={'solid'}
                    colorScheme={'primary'}
                    type="button"
                    onClick={() => {
                      // Check authentication first, before form validation
                      if (!isActive) {
                        openAuthModal({
                          onAuthSuccess: () => {
                            const submitButton = document.querySelector(
                              'button[data-formik-submit="true"]',
                            ) as HTMLButtonElement;
                            if (submitButton) {
                              submitButton.click();
                            }
                          },
                        });
                        return;
                      }
                      const submitButton = document.querySelector(
                        'button[data-formik-submit="true"]',
                      ) as HTMLButtonElement;
                      if (submitButton) {
                        submitButton.click();
                      }
                    }}>
                    {submitButtonTitle}
                  </Button>
                  {/* Hidden submit button for Formik */}
                  <button
                    type="submit"
                    data-formik-submit="true"
                    style={{ display: 'none' }}>
                    Hidden Submit
                  </button>
                </div>
              </div>
            </Form>
            <div className="mt-2 space-y-1">
              <ErrorMessage
                component="div"
                className="text-red-500 text-sm"
                name={'dateRange'}
              />
              <ErrorMessage
                component="div"
                className="text-red-500 text-sm"
                name={'pickupTimeBlock'}
              />
              <ErrorMessage
                component="div"
                className="text-red-500 text-sm"
                name={'dropoffTimeBlock'}
              />
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export default RentFormView;
